/* Capsi quiz — branching assessment ported from the v12 prototype.
 *
 * Scope of THIS file: the quiz questions + the "thinking" loading interstitial.
 * The results experience lives on a separate template (/pages/quiz-results),
 * so the prototype's inline results / partner / done routes are intentionally
 * NOT ported here. When the loading page finishes we derive a recommendation,
 * fire the production wiring, and redirect.
 *
 * Privacy by design: raw answers live ONLY in this browser. On completion the
 * ONLY things sent off-device are the DERIVED signals (primary_concern,
 * segment, recommended_pack) plus the email/consent the visitor gives — never
 * the raw health answers. The full answer set is stored locally for the
 * results page to read.
 *
 * On completion:
 *   1. buildResults() turns answers into a pack recommendation (already run on
 *      the email step) + a back-compat legacy signal set.
 *   2. Klaviyo (client-side, public ID only): identify(email, signals) +
 *      track('Completed Quiz', signals) — fires for non-buyers too.
 *   3. set cart attributes so they land on the eventual order.
 *   4. store the result (session + persistent) and redirect to the results page.
 */
(function () {
'use strict';

var root = document.querySelector('[data-quiz-root]');
if (!root) return;

/* ---- config (from the Liquid shell + theme settings) ---- */
var RESULTS_URL = root.getAttribute('data-results-url') || '/pages/quiz-results';
var PRIVACY_URL = root.getAttribute('data-privacy-url') || '/policies/privacy-policy';
var KLAVIYO_ID = (window.theme && window.theme.klaviyoPublicId) || '';
var KLAVIYO_LIST = (window.theme && window.theme.klaviyoListId) || '';

/* storage keys (RESULT_KEY / DONE_KEY are read by quiz-results.js) */
var SKEY = 'capsi_quiz_progress_v1';     /* sessionStorage — in-progress answers */
var RESULT_KEY = 'capsi_quiz_result';    /* sessionStorage — current tab handoff */
var DONE_KEY = 'capsi_quiz_result_v1';   /* localStorage  — persists across visits */

/* Second-profile ("add another person") run state, set at boot from ?addprofile=1. */
var ADDING_PROFILE = false;              /* true when this run is an extra profile on an existing order */
var BUYER_EMAIL = '';                    /* the buyer's email from the first profile — reused, not re-asked */
var BUYER_EMAIL_KEY = 'capsi_quiz_buyer_email';
var EXISTING_NAMES = [];                 /* first names already in the order — block confusing duplicates */

/* ===================================================================
   DATA
=================================================================== */
/* Which packs already contain a supplement someone might be buying separately.
   Used to flag "you can stop buying your own X" on results — we never change a
   pack's formulation, we just flag the overlap. Illustrative until the real
   per-pack ingredient list is wired from the supplier sheet. */
var PACK_CONTAINS = {
  "Women's Wellness":["Vit D","Multivitamin"],"Men's Wellness":["Vit D","Multivitamin"],
  "Vegan Nutrient":["Vit D"],"Daily Foundation":["Vit D","Multivitamin"],
  "Sleep":["Magnesium"],"Stress":["Magnesium"],
  "Perimenopause":["Magnesium"],"Bone Builder":["Vit D","Magnesium"],
  "Joint Health":["Omega-3"],
  "Gut":["Probiotic"],"Microbiome":["Probiotic"]
};

var AREAS = [
  {id:"mind",  lab:"Mind, Energy & Sleep"},
  {id:"body",  lab:"Body & Organ Health"},
  {id:"horm",  lab:"Hormones & Cycle"},
  {id:"looks", lab:"Physical Appearance"}
];
/* Which area cards show depends on sex. Men get a hormones card with different
   framing. Anyone who didn't pick male or female doesn't see a hormones card,
   since every hormone pack is made specifically for one sex. */
function areasFor(sex){
  var out=[];
  AREAS.forEach(function(a){
    if(a.id==="horm"){
      if(sex==="Female") out.push({id:"horm",lab:"Hormones & Cycle"});
      else if(sex==="Male") out.push({id:"horm",lab:"Hormones & Libido"});
    } else out.push(a);
  });
  return out;
}
var FOUND = {id:"found", lab:"No specific focus, just a strong daily foundation", csub:"the everyday basics, covered"};
var AREA_NAME = {mind:"Mind, Energy & Sleep",body:"Body & Organ Health",horm:"Hormones & Cycle",looks:"Physical Appearance",found:"Daily foundation"};

/* Location → winter sun tier, used only to decide how much the vitamin D in the
   daily base matters. Low-sun (northern) states map to "long" (little winter sun);
   high-sun (southern) to "mild"; everything else "mid". */
var US_STATES = [["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"]];
/* Five most-populous states, offered as quick-select chips above the dropdown. */
var TOP_STATES = [["CA","California"],["TX","Texas"],["FL","Florida"],["NY","New York"],["PA","Pennsylvania"]];
var LOW_SUN_STATES = ["AK","ME","NH","VT","MA","RI","CT","NY","NJ","PA","OH","MI","WI","MN","ND","SD","MT","ID","WA","OR","WY","IA","NE","IL","IN","CO","UT","WV","MD","DE"];
var HIGH_SUN_STATES = ["FL","TX","AZ","NM","CA","NV","LA","MS","AL","GA","SC","HI"];
function sunTierFor(abbr){ if(LOW_SUN_STATES.indexOf(abbr)>=0) return "long"; if(HIGH_SUN_STATES.indexOf(abbr)>=0) return "mild"; return "mid"; }

/* Step 2 is one combined list. Each "concern" maps straight to a pack. leavesFor
   returns the concerns to show for a given area based on sex and age. */
function leavesFor(area, sex, age){
  switch(area){
    case "mind": return [
      {key:"energy",   label:"Low energy through the day",        pack:"Energy"},
      {key:"sleep",    label:"Trouble sleeping",                  pack:"Sleep"},
      {key:"stress",   label:"Feeling wired or tense",            pack:"Stress"},
      {key:"focus",    label:"Focus and mental clarity",          pack:"Focus"},
      {key:"mood",     label:"Low or anxious mood",               pack:"Mood Support"},
      {key:"longevity",label:"Aging well and protecting my cells",pack:"Longevity"}
    ];
    case "body": return [
      {key:"gut",      label:"Bloating and discomfort",           pack:"Gut"},
      {key:"micro",    label:"Better overall gut health",         pack:"Microbiome"},
      {key:"immune",   label:"I catch colds and bugs easily",     pack:"Immune"},
      {key:"joints",   label:"Joint aches and stiffness",         pack:"Joint Health"},
      {key:"allergy",  label:"Seasonal allergies",                pack:"Allergy"},
      {key:"bones",    label:"Bone strength",                     pack:"Bone Builder"}
    ];
    case "horm":
      if(sex==="Male") return [
        {key:"mhorm",  label:"Energy, libido and strength",       pack:"Men's Hormone"}
      ];
      return [
        {key:"pms",    label:"Cramps, mood and bloating before my period", pack:"PMS"},
        {key:"whorm",  label:"Overall balance, mood and libido",          pack:"Women's Hormone"},
        {key:"peri",   label:"Hot flashes and changing cycles", pack:"Perimenopause",
           tag:(age==="40s"||age==="50s")?"Common in your "+age:null}
      ];
    case "looks": return [
      {key:"skin",     label:"Clearer skin and fewer breakouts",  pack:"Clear Skin"},
      {key:"hair",     label:"Thinning or shedding hair",         pack:"Hair"},
      {key:"beauty",   label:"Fine lines and aging",              pack:"Beauty"}
    ];
    default: return [];
  }
}

var PAIRS = {
  "Sleep":["Stress","FOUNDATION"],
  "Stress":["Sleep","FOUNDATION"],
  "Mood Support":["Sleep","FOUNDATION"],
  "Energy":["FOUNDATION","Brain"],
  "Performance":["FOUNDATION","Joint Health"],
  "Longevity":["Antioxidant","FOUNDATION"],
  "Focus":["Brain","FOUNDATION"],
  "Brain":["Focus","FOUNDATION"],
  "PMS":["Mood Support","FOUNDATION"],
  "Women's Hormone":["Mood Support","FOUNDATION"],
  "Perimenopause":["Bone Builder","Mood Support"],
  "Men's Hormone":["Men's Wellness","Energy"],
  "Gut":["Immune","FOUNDATION"],
  "Microbiome":["Immune","FOUNDATION"],
  "Clear Skin":["Microbiome","FOUNDATION"],
  "Beauty":["Hair","FOUNDATION"],
  "Hair":["Beauty","FOUNDATION"],
  "Joint Health":["Bone Builder","FOUNDATION"],
  "Bone Builder":["Joint Health","FOUNDATION"],
  "Immune":["Gut","FOUNDATION"],
  "Allergy":["Immune","Gut"]
};
var FALLBACK_PAIRS = ["Antioxidant","Mineral","FOUNDATION"];

/* small line-art icons (inherit colour via currentColor) */
var ICON = {
  shelf:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" stroke-linecap="round"><rect x="4" y="8.5" width="3.4" height="10.5" rx=".8"/><path d="M4.4 8.5V7h2.6v1.5"/><rect x="10.3" y="6" width="3.4" height="13" rx=".8"/><path d="M10.7 6V4.5h2.6V6"/><rect x="16.6" y="10" width="3.4" height="9" rx=".8"/><path d="M17 10V8.5h2.6V10"/></svg>',
  multi:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><rect x="7.5" y="7" width="9" height="13" rx="1.6"/><path d="M9 7V4.5h6V7"/><path d="M12 11.5v4M10 13.5h4"/></svg>',
  none:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><circle cx="12" cy="12" r="7.5"/><path d="M6.8 17.2 17.2 6.8"/></svg>',
  onoff:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M18.7 7.6A7 7 0 1 0 19 14"/><path d="M19 4.4v3.4h-3.4"/></svg>'
};

/* Deep-clone helper: structuredClone isn't available on Safari <15.4, so
   feature-detect once and fall back to the JSON round-trip (fine here since
   STATE is plain JSON-safe data — no Dates/Maps/etc). */
var clone = (typeof structuredClone === "function")
  ? structuredClone
  : function(v){ return JSON.parse(JSON.stringify(v)); };

/* ===================================================================
   STATE + NAV
=================================================================== */
var STATE, current, history;

function fresh(){
  return {
    q1:null, gender:null, sex:null, age:null,
    areasOrdered:[], areaStack:[], extraAreas:[],
    goals:[],                 // {role:'primary'|'secondary', pack | 'FOUNDATION'}
    diet:null, restrict:[], lowEat:[], eatRegular:[], activity:null, sleep:null, stress:null, crisis:null, sun:null, usState:null,
    meds:null, medKinds:[], medOther:"", pregnancy:null, conditions:[], currentSupps:[],
    name:"", email:"", optMarketing:false,
    sleepMelatonin:false, melatoninScreened:false, melatoninLevel:null, melatoninReason:""
  };
}

/* Weighted progress: front-loaded so early answers feel like real movement,
   then steady (smaller) steps through the safety section. */
var WEIGHTS={q1:12,q2:18,q3:24,q4areas:32,q5goals:45,qDiet:52,qActivity:58,
  q10:62,q11:66,qMood:66,qHelp:66,qSun:70,q12:74,q12kinds:78,q13:81,q14:84,
  q15:87,q16restrict:89,q17:92,q18:96,processing:99};
function progressPct(){ return WEIGHTS[current]!=null ? WEIGHTS[current] : 100; }
function showProgress(){ return true; }

/* Analytics stub: pushes to the GTM dataLayer when present, no-op otherwise.
   Also forwards every event to Shopify customer events so custom pixels can
   subscribe. quiz_step is included for GA4 funnel/drop-off analysis; it stays
   out of Meta because the Meta pixel simply doesn't subscribe to it (only
   custom pixels that call analytics.subscribe by name receive an event). */
function track(ev,props){
  try{ (window.dataLayer=window.dataLayer||[]).push(Object.assign({event:ev},props||{})); }catch(e){}
  try{
    if(window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish==="function"){
      Shopify.analytics.publish(ev,props||{});
    }
  }catch(e){}
}

/* Session persistence: answers survive a refresh or accidental navigation. */
function saveSession(){
  try{ sessionStorage.setItem(SKEY,JSON.stringify({STATE:STATE,current:current,history:history})); }catch(e){}
}
function loadSession(){
  try{
    var d=JSON.parse(sessionStorage.getItem(SKEY)||"null");
    if(d&&d.current&&d.STATE){
      STATE=d.STATE; history=d.history||[];
      current=d.current;
      return true;
    }
  }catch(e){}
  return false;
}

function go(next, mutate){
  history.push({current:current, state:clone(STATE)});
  if(mutate) mutate();
  current = next;
  try{ window.history.pushState({i:history.length},""); }catch(e){}
  track("quiz_step",{step:next,index:history.length});
  render();
}
function internalBack(){
  if(!history.length) return;
  var prev=history.pop(); current=prev.current; STATE=prev.state; render();
}
/* The in-quiz back button and the browser/OS back gesture do the same thing. */
var suppressPop=false;
function back(){
  if(!history.length) return;
  internalBack();
  suppressPop=true;
  try{ window.history.back(); }catch(e){ suppressPop=false; }
}
window.addEventListener("popstate",function(e){
  if(suppressPop){ suppressPop=false; return; }
  var idx=(e.state&&typeof e.state.i==="number")?e.state.i:0;
  if(idx<history.length) internalBack();
  else { try{ window.history.replaceState({i:history.length},""); }catch(err){} }
});

/* ===================================================================
   RENDER
=================================================================== */
var screen = document.getElementById('screen');
var lastRoute=null;

function render(){
  var pw=document.querySelector('.prog-wrap'), pct=progressPct();
  document.getElementById('prog').style.right=(100-pct)+"%";
  pw.style.opacity = showProgress()?1:0;
  pw.setAttribute("role","progressbar");
  pw.setAttribute("aria-label","Assessment progress");
  pw.setAttribute("aria-valuemin","0");
  pw.setAttribute("aria-valuemax","100");
  pw.setAttribute("aria-valuenow",String(pct));
  // step indicator in the header (ordinal; hidden on the loader)
  var sn=document.getElementById('stepnum');
  if(sn) sn.textContent = (current==="processing") ? "" : (current==="q18" ? "Last step" : ("Question "+(history.length+1)));

  var r = ROUTES[current] ? ROUTES[current]() : {title:"",body:""};
  // tear down any listeners the previous screen registered (e.g. scroll cue)
  if(screen._cleanup){ try{ screen._cleanup(); }catch(e){} screen._cleanup=null; }
  var sameRoute = lastRoute===current;
  // toggling a checkbox re-renders the same screen: skip the entrance animation
  // and remember which control had focus so we can hand it back
  screen.classList.toggle("no-anim", sameRoute);
  var ae=document.activeElement;
  var focusVal = (sameRoute && ae && screen.contains(ae)) ? ae.getAttribute("data-value") : null;

  // header controls: Back (step back) shows once there's history; Exit hides on the loader
  var qb=document.getElementById('qzback');
  if(qb) qb.hidden = !(history.length && current!=="processing");
  var qe=document.getElementById('qzexit');
  if(qe) qe.style.visibility = (current==="processing") ? "hidden" : "";
  screen.innerHTML = r.body;

  // on navigation, move focus to the question so keyboard / screen-reader users
  // land in the right place (input screens re-take focus in their mount below)
  if(!sameRoute && lastRoute!==null){
    var h=screen.querySelector("h1.q, .proc-wrap h1");
    if(h){ h.setAttribute("tabindex","-1"); h.focus({preventScroll:true}); }
  }
  if(r.mount) r.mount();
  if(sameRoute && focusVal!==null){
    var el=screen.querySelector('[data-value="'+((window.CSS&&CSS.escape)?CSS.escape(focusVal):focusVal)+'"]');
    if(el) el.focus({preventScroll:true});
  }
  lastRoute=current;
  saveSession();
}

function optList(opts, selectedVal, multi){
  return '<div class="opts">'+opts.map(function(o){
    var sel = multi ? (selectedVal||[]).indexOf(o.value)>=0 : selectedVal===o.value;
    return '<button class="opt '+(multi?'multi':'')+' '+(sel?'sel':'')+'" data-act="'+(multi?'toggle':'pick')+'" data-value="'+o.value+'">'
      +(o.icon?'<span class="ic">'+o.icon+'</span>':'')
      +'<span class="lab">'+o.label+(o.sub?'<span class="sub">'+o.sub+'</span>':'')+'</span>'
      +(o.tag?'<span class="tag">'+o.tag+'</span>':'')
      +'<span class="mk"></span>'
    +'</button>';
  }).join("")+'</div>';
}

var ROUTES = {
  /* ---------- PART 1 ---------- */
  q1:function(){return{
    body:
      '<div class="qhead"><h1 class="q">Do you take any vitamins right now?</h1></div>'
      +'<div class="opts">'
      +[
        {value:"multi",label:"Just a multivitamin",icon:ICON.multi},
        {value:"cabinet",label:"Lots of separate vitamins or supplements",icon:ICON.shelf},
        {value:"onoff",label:"I'm not very consistent",sub:"I tend to start and stop",icon:ICON.onoff},
        {value:"none",label:"Nothing right now",icon:ICON.none}
      ].map(function(o){return '<button class="opt '+(STATE.q1===o.value?'sel':'')+'" data-act="pick" data-value="'+o.value+'">'
          +'<span class="ic">'+o.icon+'</span>'
          +'<span class="lab">'+o.label+(o.sub?'<span class="sub">'+o.sub+'</span>':'')+'</span>'
          +'<span class="mk"></span>'
        +'</button>';}).join("")
      +'</div>',
    mount:function(){ pickHandler(function(v){go("q2",function(){STATE.q1=v;});}); }
  };},

  q2:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">Are you biologically male or female?</h1>'
        +'<div class="qhint"><span class="i">i</span><span>Based on scientific research supplement packs are formulated differently for women and men.</span></div>'
      +'</div>'
      +optList([
        {value:"female",label:"Female"},
        {value:"male",label:"Male"},
        {value:"prefer",label:"Prefer not to say"}
      ], STATE.gender),
    mount:function(){ pickHandler(function(v){go("q3",function(){ STATE.gender=v; STATE.sex=computeSexBucket(); });}); }
  };},

  q3:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">How old are you?</h1>'
        +'<div class="qhint"><span>You need to be 18 or over to use Capsi.</span></div>'
      +'</div>'
      +optList([
        {value:"18-29",label:"18 to 29"},{value:"30s",label:"30 to 39"},
        {value:"40s",label:"40 to 49"},{value:"50s",label:"50 to 59"},{value:"60+",label:"60 or over"}
      ], STATE.age),
    mount:function(){ pickHandler(function(v){go("q4areas",function(){STATE.age=v;});}); }
  };},

  /* ---------- GOAL STEP 1: pick area(s) ---------- */
  q4areas:function(){
    var ord = STATE.areasOrdered;
    var atCap = ord.filter(function(x){return x!=="found";}).length>=3;
    return{
      body:
        '<div class="qhead">'
          +'<h1 class="q">What would you like to work on?</h1>'
          +'<div class="qhint"><span>Choose up to 3 areas to focus on.</span></div>'
        +'</div>'
        +'<div class="opts">'
          +areasFor(STATE.sex).map(function(a){
            var sel = ord.indexOf(a.id)>=0;
            return '<button class="opt multi '+(sel?'sel':'')+' '+(atCap&&!sel?'capped':'')+'" data-act="area" data-value="'+a.id+'">'
              +'<span class="lab">'+a.lab+'</span><span class="mk"></span></button>';
          }).join("")
          +'<button class="opt multi '+(ord.indexOf('found')>=0?'sel':'')+'" data-act="area" data-value="found">'
            +'<span class="lab">'+FOUND.lab+'<span class="sub">'+FOUND.csub+'</span></span><span class="mk"></span>'
          +'</button>'
        +'</div>'
        +'<div class="caphint" id="caphint" hidden>Three areas keeps your set focused. Unselect one to swap it.</div>'
        +'<div class="spacer-grow"></div>'
        +'<div class="cta-wrap"><button class="cta" data-act="next" '+(ord.length?'':'disabled')+'>Continue</button></div>',
      mount:function(){
        var hint=screen.querySelector('#caphint');
        screen.querySelectorAll('[data-act="area"]').forEach(function(b){b.onclick=function(){
          var id=b.dataset.value, a=STATE.areasOrdered, i=a.indexOf(id);
          if(i<0){
            // soft cap: at most 3 real focus areas (the daily-foundation option doesn't count)
            if(id!=="found" && a.filter(function(x){return x!=="found";}).length>=3){
              if(hint){ hint.hidden=false; clearTimeout(hint._t); hint._t=setTimeout(function(){hint.hidden=true;},2400); }
              return;
            }
            a.push(id);
          } else a.splice(i,1);
          render();
        };});
        screen.querySelector('[data-act="next"]').onclick=function(){
          if(!STATE.areasOrdered.length) return;
          var real = STATE.areasOrdered.filter(function(a){return a!=="found";});
          if(!real.length){
            // foundation only: nothing to narrow, the daily base is the whole set
            var goals=[{role:"primary",pack:"FOUNDATION"}];
            go(afterGoals(goals),function(){ STATE.goalPacks=[]; STATE.goals=goals; });
          } else {
            go("q5goals",function(){ STATE.goalPacks=[]; });
          }
        };
      }
    };
  },

  /* ---------- GOAL STEP 2: one combined list of concerns ---------- */
  q5goals:function(){
    if(!STATE.goalPacks) STATE.goalPacks=[];
    var areas = STATE.areasOrdered.filter(function(a){return a!=="found";});
    var areaLabels={}; areasFor(STATE.sex).forEach(function(a){areaLabels[a.id]=a.lab;});
    var groups = areas.map(function(a){return {name:areaLabels[a]||AREA_NAME[a], leaves:leavesFor(a, STATE.sex, STATE.age)};});
    var list = groups.map(function(g){return ''
        +'<div class="grouplbl">'+g.name+'</div>'
        +'<div class="opts">'
          +g.leaves.map(function(l){
            var sel = STATE.goalPacks.indexOf(l.pack)>=0;
            return '<button class="opt multi '+(sel?'sel':'')+'" data-act="leaf" data-value="'+l.pack+'">'
              +'<span class="lab">'+l.label+'</span>'
              +(l.tag?'<span class="tag">'+l.tag+'</span>':'')
              +'<span class="mk"></span>'
            +'</button>';
          }).join("")
        +'</div>';}).join("");
    return{
      body:
        '<div class="qhead">'
          +'<h1 class="q">Which of these matter most to you?</h1>'
        +'</div>'
        +list
        +'<div class="scrollcue" id="scrollcue"><span>More below</span></div>'
        +'<div class="spacer-grow"></div>'
        +'<div class="cta-wrap"><button class="cta" data-act="next" '+(STATE.goalPacks.length?'':'disabled')+'>Continue</button></div>',
      mount:function(){
        screen.querySelectorAll('[data-act="leaf"]').forEach(function(b){b.onclick=function(){
          var p=b.dataset.value, a=STATE.goalPacks, i=a.indexOf(p);
          if(i<0) a.push(p); else a.splice(i,1);
          render();
        };});
        screen.querySelector('[data-act="next"]').onclick=function(){
          if(!STATE.goalPacks.length) return;
          var goals = STATE.goalPacks.map(function(p,idx){return {role:idx===0?"primary":"secondary", pack:p};});
          go("qDiet",function(){ STATE.goals = goals; });
        };

        // "more below" cue: only relevant with 2+ areas (one area never overflows).
        var cue=screen.querySelector('#scrollcue');
        if(groups.length<2){ if(cue) cue.remove(); return; }
        var allOpts=screen.querySelectorAll('.opts');
        var lastList=allOpts[allOpts.length-1];
        var ctaWrap=screen.querySelector('.cta-wrap');
        var update=function(){
          if(!lastList) return;
          var r=lastList.getBoundingClientRect();
          // in phone view the sticky CTA covers the bottom of the viewport, so
          // the "end of the list" sits above it, not at the window edge
          var occlude=(root.dataset.view==="phone" && ctaWrap)?ctaWrap.offsetHeight:0;
          var moreBelow=r.bottom > (window.innerHeight||document.documentElement.clientHeight)-occlude-8;
          cue.classList.toggle('show', moreBelow);
        };
        cue.querySelector('span').onclick=function(){
          (lastList||cue).scrollIntoView({behavior:'smooth',block:'end'});
        };
        window.addEventListener('scroll',update,{passive:true});
        window.addEventListener('resize',update);
        screen._cleanup=function(){ window.removeEventListener('scroll',update); window.removeEventListener('resize',update); };
        requestAnimationFrame(update);
      }
    };
  },

  /* ---------- lifestyle: diet + activity ---------- */
  qDiet:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">Which of these do you eat regularly?</h1>'
        +'<div class="qhint"><span>Select all that apply. A lot of what your body needs comes from food, so if something\'s usually missing, we fill that in.</span></div>'
      +'</div>'
      +optList([
        {value:"greens",label:"Leafy greens"},
        {value:"fish",label:"Fish or seafood"},
        {value:"dairy",label:"Dairy"},
        {value:"redmeat",label:"Red meat"}
      ], STATE.eatRegular, true)
      +'<div class="spacer-grow"></div>'
      +'<div class="cta-wrap"><button class="cta" data-act="next" '+((STATE.eatRegular||[]).length?'':'disabled')+'>Continue</button></div>',
    mount:function(){
      toggleHandler("eatRegular");
      var n=screen.querySelector('[data-act="next"]');
      n.onclick=function(){
        if(!(STATE.eatRegular||[]).length) return;
        // logic flip: what they DON'T eat regularly is the gap we fill
        var all=["greens","fish","dairy","redmeat"];
        STATE.lowEat = all.filter(function(f){return (STATE.eatRegular||[]).indexOf(f)<0;});
        go("qActivity");
      };
    }
  };},

  qActivity:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">Which of the below best describes your activity level?</h1>'
      +'</div>'
      +optList([
        {value:"sitting",label:"I spend a lot of time sat down"},
        {value:"onfeet",label:"I'm on my feet a lot, but I don't work out"},
        {value:"active",label:"I train or work out regularly"},
        {value:"athlete",label:"Working out is my main hobby"}
      ], STATE.activity),
    mount:function(){ pickHandler(function(v){go(afterGoals(),function(){STATE.activity=v;});}); }
  };},

  /* ---------- lifestyle: sleep + stress (asked only when relevant) ---------- */
  q10:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">How many hours of sleep do you get a night, on average?</h1>'
      +'</div>'
      +optList([
        {value:"lt5",label:"Less than 5 hours"},
        {value:"56",label:"5-6 hours"},
        {value:"78",label:"7-8 hours"},
        {value:"8plus",label:"8 hours+"}
      ], STATE.sleep),
    mount:function(){ pickHandler(function(v){go(afterSleep(),function(){STATE.sleep=v;});}); }
  };},

  q11:function(){return{
    body:
      '<div class="qhead"><h1 class="q">Over the last 3 months, what\'s been your average stress level?</h1></div>'
      +optList([
        {value:"low",label:"Low"},
        {value:"elevated",label:"Reasonable, but definitely elevated"},
        {value:"high",label:"High, or difficult to manage"}
      ], STATE.stress),
    mount:function(){ pickHandler(function(v){go(afterStress(),function(){STATE.stress=v;});}); }
  };},

  /* Shown only to people working on mood: a gentle, mandatory safety check. */
  qMood:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">Over the past two weeks, have you had thoughts of suicide or of harming yourself?</h1>'
        +'<div class="qhint"><span>There\'s no wrong answer. We only want to make sure you\'re okay.</span></div>'
      +'</div>'
      +optList([{value:"No",label:"No"},{value:"Yes",label:"Yes"}], STATE.crisis),
    mount:function(){ pickHandler(function(v){
      if(v==="Yes") go("qHelp",function(){STATE.crisis="Yes";});
      else go("qSun",function(){STATE.crisis="No";});
    });}
  };},

  /* Resource screen if they answered Yes above. Supportive, never dismissive. */
  qHelp:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">We\'re really glad you told us.</h1>'
        +'<div class="qhint"><span>What you\'re feeling matters, and you deserve support from people trained to help. Capsi can\'t be that, but help is here right now, day or night.</span></div>'
      +'</div>'
      +'<div class="setaside" style="text-align:left;line-height:1.55">'
        +'<div><b>Call or text 988</b> for the 988 Suicide &amp; Crisis Lifeline (US). Free, 24/7.</div>'
        +'<div style="margin-top:9px"><b>Text HOME to 741741</b> for the Crisis Text Line.</div>'
        +'<div style="margin-top:9px">If you\'re in immediate danger, call 911 or go to your nearest emergency room.</div>'
      +'</div>'
      +'<p style="font-size:12.5px;color:var(--muted);margin-top:14px;text-align:left">There\'s no rush. You can pick this back up whenever you\'re ready.</p>'
      +'<div class="spacer-grow"></div>'
      +'<div class="cta-wrap"><button class="cta" data-act="next">Continue</button></div>',
    mount:function(){ screen.querySelector('[data-act="next"]').onclick=function(){go("qSun");}; }
  };},

  /* Asked of everyone: a low-friction read on sunlight, used only to explain the
     vitamin D already in the daily base for people in low-sun places. */
  qSun:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">Which state do you live in?</h1>'
        +'<div class="qhint"><span>Sunlight is vital to the amount of vitamin D your body can produce. Based on your location, we can tailor our recommendation to you.</span></div>'
      +'</div>'
      +'<div class="statechips">'
        +TOP_STATES.map(function(s){return '<button class="statechip'+(STATE.usState===s[0]?' on':'')+'" data-state="'+s[0]+'">'+s[1]+'</button>';}).join("")
      +'</div>'
      +'<div class="selectwrap">'
        +'<select class="field select" id="ussel">'
          +'<option value=""'+(STATE.usState?'':' selected')+' disabled>Or choose another state</option>'
          +US_STATES.map(function(p){return '<option value="'+p[0]+'"'+(STATE.usState===p[0]?' selected':'')+'>'+p[1]+'</option>';}).join("")
        +'</select>'
      +'</div>'
      +'<div class="spacer-grow"></div>'
      +'<div class="cta-wrap"><button class="cta" data-act="next" '+(STATE.usState?'':'disabled')+'>Continue</button></div>',
    mount:function(){
      var sel=screen.querySelector('#ussel'), btn=screen.querySelector('[data-act="next"]');
      var chips=[].slice.call(screen.querySelectorAll('.statechip'));
      var setState=function(v){
        STATE.usState=v||null; STATE.sun=v?sunTierFor(v):null; btn.disabled=!v;
        chips.forEach(function(c){ c.classList.toggle('on', c.dataset.state===v); });
      };
      chips.forEach(function(c){ c.onclick=function(){ sel.value=c.dataset.state; setState(c.dataset.state); }; });
      sel.onchange=function(){ setState(sel.value); };
      btn.onclick=function(){ if(STATE.usState) go("q12"); };
    }
  };},

  /* ---------- safety ---------- */
  q12:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">Do you currently take any prescription medication?</h1>'
      +'</div>'
      +optList([{value:"No",label:"No"},{value:"Yes",label:"Yes"}], STATE.meds),
    mount:function(){ pickHandler(function(v){
      if(v==="Yes") go("q12kinds",function(){STATE.meds="Yes";});
      else go(nextAfterMeds(),function(){STATE.meds="No";STATE.medKinds=[];});
    });}
  };},

  q12kinds:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">What types of medication are you taking?</h1>'
      +'</div>'
      +optList([
        {value:"ssri",label:"Antidepressants / SSRIs"},
        {value:"thyroid",label:"Thyroid (like levothyroxine)"},
        {value:"glp1",label:"Diabetes / GLP-1"},
        {value:"bp",label:"Blood pressure"},
        {value:"bloodthinner",label:"Blood thinners"},
        {value:"other",label:"Something else"},
        {value:"prefer",label:"Rather not say"}
      ], STATE.medKinds, true)
      +((STATE.medKinds||[]).indexOf("other")>=0?'<input class="field" id="medother" placeholder="Tell us which, if you\'d like (optional)" value="'+esc(STATE.medOther||'')+'">':'')
      +'<div class="spacer-grow"></div>'
      +'<div class="cta-wrap"><button class="cta" data-act="next">Continue</button></div>',
    mount:function(){
      toggleHandler("medKinds");
      var mo=screen.querySelector('#medother'); if(mo){ mo.oninput=function(){STATE.medOther=mo.value;}; }
      screen.querySelector('[data-act="next"]').onclick=function(){go(nextAfterMeds());};
    }
  };},

  q13:function(){return{
    body:
      '<div class="qhead"><h1 class="q">Are you pregnant, breastfeeding, or trying to conceive?</h1></div>'
      +optList([{value:"No",label:"No"},{value:"Yes",label:"Yes"}], STATE.pregnancy)
      +'<div class="setaside" id="pregnote"'+(STATE.pregnancy==="Yes"?"":" hidden")+'>We don\'t make a prenatal pack yet, so we\'ll show you what\'s safe and flag anything to skip.</div>'
      +'<div class="spacer-grow"></div>'
      +'<div class="cta-wrap" id="pregcta"'+(STATE.pregnancy==="Yes"?"":" hidden")+'><button class="cta" data-act="pregnext">Continue</button></div>',
    mount:function(){
      var note=screen.querySelector('#pregnote'), cta=screen.querySelector('#pregcta');
      var advancing=false;
      screen.querySelectorAll('[data-act="pick"]').forEach(function(b){b.onclick=function(){
        var v=b.dataset.value;
        screen.querySelectorAll('.opt').forEach(function(o){o.classList.toggle('sel', o===b);});
        STATE.pregnancy=v;
        if(v==="No"){ if(advancing) return; advancing=true; setTimeout(function(){go(nextAfterPregnancy());},200); }
        else { note.hidden=false; cta.hidden=false; }
      };});
      var nb=screen.querySelector('[data-act="pregnext"]');
      if(nb) nb.onclick=function(){go(nextAfterPregnancy());};
    }
  };},

  /* shown only when one of the packs you're likely to see could be affected */
  q14:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">Do any of these apply to you?</h1>'
        +'<div class="qhint"><span>Some ingredients aren\'t suitable with certain conditions, so we check before recommending them.</span></div>'
      +'</div>'
      +optList([
        {value:"thyroidcond",label:"Thyroid condition"},
        {value:"autoimmune",label:"Autoimmune condition",sub:"like lupus, RA, MS, or Hashimoto's"},
        {value:"kidney",label:"Kidney issues"},
        {value:"none",label:"None of these"}
      ], STATE.conditions, true)
      +'<div class="spacer-grow"></div>'
      +'<div class="cta-wrap"><button class="cta" data-act="next">Continue</button></div>',
    mount:function(){ toggleHandler("conditions","none"); nextBtn(q15OrSkip()); }
  };},

  q15:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">Which of these are you already taking?</h1>'
        +'<div class="qhint"><span>If one of our packs already contains it, we\'ll point that out so you don\'t double up.</span></div>'
      +'</div>'
      +optList([
        {value:"Vit D",label:"Vitamin D"},{value:"Multivitamin",label:"A multivitamin"},
        {value:"Magnesium",label:"Magnesium"},{value:"Omega-3",label:"Omega-3"},
        {value:"Probiotic",label:"Probiotic"},{value:"Nothing",label:"None of these"}
      ], STATE.currentSupps, true)
      +'<div class="spacer-grow"></div>'
      +'<div class="cta-wrap"><button class="cta" data-act="next">Continue</button></div>',
    mount:function(){ toggleHandler("currentSupps","Nothing"); nextBtn("q16restrict"); }
  };},

  q16restrict:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">Do you have any dietary requirements we should be aware of?</h1>'
        +'<div class="qhint"><span>We only ask about the ones that change which packs we\'d recommend, so don\'t worry if yours isn\'t listed here.</span></div>'
      +'</div>'
      +optList([
        {value:"vegan",label:"Vegan"},
        {value:"shellfish",label:"Shellfish allergy",sub:"we check every ingredient in every pack"},
        {value:"soy",label:"Soy allergy",sub:"used in some softgels"},
        {value:"none",label:"No restrictions"}
      ], STATE.restrict, true)
      +'<div class="spacer-grow"></div>'
      +'<div class="cta-wrap"><button class="cta" data-act="next">Continue</button></div>',
    mount:function(){
      toggleHandler("restrict","none");
      screen.querySelector('[data-act="next"]').onclick=function(){
        var r=STATE.restrict||[];
        STATE.diet = r.indexOf("vegan")>=0?"vegan":"omni";
        go("q17");
      };
    }
  };},

  /* ---------- capstone: name + email ----------
     The name is OPTIONAL on a normal run (the results page, loader and Klaviyo
     greeting all fall back cleanly without it). It stays required on an
     add-a-profile run, where the name is what tells two people's packs apart. */
  q17:function(){return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">'+(ADDING_PROFILE?'Who is this profile for?':'What should we call you?')+'</h1>'
        +'<div class="qhint"><span>'+(ADDING_PROFILE?'We’ll use this name on their results. Your own packs stay saved in your order.':'Optional. We’ll use it on your results page.')+'</span></div>'
      +'</div>'
      +'<input class="field" id="nm" placeholder="'+(ADDING_PROFILE?'Their first name':'First name (optional)')+'" value="'+esc(STATE.name)+'" autocomplete="given-name">'
      +'<div class="fieldhelp" id="nmhelp" hidden>Someone with that name is already in your order. Add a last initial so we can tell them apart.</div>'
      +'<div class="spacer-grow"></div>'
      +'<div class="cta-wrap"><button class="cta" data-act="next"'+(ADDING_PROFILE?' disabled':'')+'>Continue</button></div>',
    mount:function(){
      var inp=screen.querySelector('#nm'), btn=screen.querySelector('[data-act="next"]'), help=screen.querySelector('#nmhelp');
      var dup=function(v){ return ADDING_PROFILE && EXISTING_NAMES.indexOf(String(v).trim().toLowerCase())>=0; };
      var ok=function(){ return (STATE.name || !ADDING_PROFILE) && !dup(STATE.name); };
      var sync=function(){ STATE.name=inp.value.trim(); var d=dup(STATE.name); btn.disabled=!ok(); if(help) help.hidden=!d; };
      inp.oninput=sync; sync();
      // don't autofocus on touch: it pops the keyboard over the sticky CTA
      if(!(window.matchMedia&&window.matchMedia('(pointer:coarse)').matches)) inp.focus();
      btn.onclick=function(){ if(ok()) nameNext(); };
      inp.onkeydown=function(e){if(e.key==="Enter"&&ok())nameNext();};
    }
  };},

  /* Email gate. Everything above the field frames the match as done and
     waiting (their answers, their goals, a locked card) WITHOUT naming the
     pack, so the email reads as the last small step rather than a toll. */
  q18:function(){
    var answered=history.length-1;                 /* q17 (name) isn’t a health answer */
    var gp=gateGoals();
    var from='Matched from '+(answered>=5?'your '+answered+' answers':'your answers')+(gp.length?' on '+andList(gp):'')+'.';
    return{
    body:
      '<div class="qhead">'
        +'<h1 class="q">'+(STATE.name?esc(STATE.name)+', your':'Your')+' match is ready.</h1>'
        +'<div class="qhint"><span>You’ve done the hard part. Enter your email to see it.</span></div>'
      +'</div>'
      +'<div class="gate">'
        +'<div class="gate-top"><span>Your match</span><span class="gate-ready"><i></i>Ready</span></div>'
        +'<div class="gate-bars" aria-hidden="true"><span></span><span></span></div>'
        +'<div class="gate-from">'+esc(from)+'</div>'
      +'</div>'
      +'<input class="field" id="em" type="email" placeholder="you@email.com" value="'+esc(STATE.email)+'" autocomplete="email" inputmode="email">'
      +'<div class="fieldhelp" id="emhelp" hidden>Enter a valid email to continue.</div>'
      +'<div class="gate-note">We’ll save your answers so you never have to retake the assessment. They stay private.</div>'
      +'<div class="spacer-grow"></div>'
      +'<div class="cta-wrap">'
        +'<button class="cta" data-act="next" disabled>See my curated selection</button>'
        +'<p class="disclosure">By continuing, you agree to receive emails from Capsi, including offers and order updates. Unsubscribe anytime. <a href="'+esc(PRIVACY_URL)+'" target="_blank" rel="noopener">Privacy policy</a>.</p>'
      +'</div>',
    mount:function(){
      var inp=screen.querySelector('#em'), btn=screen.querySelector('[data-act="next"]'), help=screen.querySelector('#emhelp');
      var ok=function(v){return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);};
      var sync=function(){STATE.email=inp.value.trim();btn.disabled=!ok(STATE.email); if(ok(STATE.email)) help.hidden=true;};
      inp.oninput=sync; sync();
      // don't autofocus on touch: it pops the keyboard over the sticky CTA
      if(!(window.matchMedia&&window.matchMedia('(pointer:coarse)').matches)) inp.focus();
      inp.onblur=function(){ if(STATE.email && !ok(STATE.email)) help.hidden=false; };
      var finish=function(){ if(ok(STATE.email)){
        STATE.optMarketing=true; // consent via disclosure (opt-out model)
        // Identity fields ride along for Meta advanced matching — the pixel
        // normalizes + SHA-256 hashes them in-browser, so raw values never
        // reach Meta. Only demographic identifiers (name/gender/state), never
        // the health answers. Once per quiz run: re-submitting after a
        // back-step must not re-fire Lead (it may be the optimization event).
        if(!STATE._leadSent){
          STATE._leadSent=true;
          track("email_submitted",{
            email:STATE.email,
            name:STATE.name||"",
            gender:STATE.gender==="female"?"f":(STATE.gender==="male"?"m":""),
            state:STATE.usState||"",
            country:"us"
          });
        }
        buildResults(); go("processing");
      } };
      btn.onclick=finish; inp.onkeydown=function(e){if(e.key==="Enter")finish();};
    }
  };},


  /* ---------- processing interstitial ----------
     Shown once, between email submit and results. Each line names a facet the
     person gave us, so it feels like a considered read of the whole person.
     5 steps, ~3–4s total, irregular timing so it reads as thought, not animation. */
  processing:function(){
    var steps = procSteps();
    return{
      body:'<div class="proc-wrap" aria-live="polite">'
        +'<h1>One moment'+(STATE.name?', '+esc(STATE.name):'')+'.</h1>'
        +steps.map(function(s){return '<div class="proc-step"><span class="pmk"></span><span>'+s+'</span></div>';}).join("")
      +'</div>',
      mount:function(){
        var rows=[].slice.call(screen.querySelectorAll('.proc-step'));
        var durs=[1050,1450,1150,950,1300]; // varied: uniform timing reads as fake
        var t=450;
        rows.forEach(function(r,i){
          setTimeout(function(){r.classList.add('show');},t);
          var last=i===rows.length-1;
          t += last ? 1600 : durs[i%durs.length]; // hold the closing beat longer
          setTimeout(function(){r.classList.add('done');},t);
          t+=180;
        });
        setTimeout(function(){
          if(!STATE._completeSent){                    /* once per quiz run */
            STATE._completeSent=true;
            var pk=primaryPackName();
            track("quiz_complete",{pack:pk,concern:concernFor(pk)});
          }
          complete();
        }, t+650);
      }
    };
  }
};

/* ---------- handlers ---------- */
function pickHandler(cb){
  var picked=false; // double-tap guard: two fast taps must not fire navigation twice
  screen.querySelectorAll('[data-act="pick"]').forEach(function(b){
    b.onclick=function(){ if(picked) return; picked=true; b.classList.add('sel'); setTimeout(function(){cb(b.dataset.value);},200); };
  });
}
function toggleHandler(field, exclusive){
  screen.querySelectorAll('[data-act="toggle"]').forEach(function(b){
    b.onclick=function(){
      var v=b.dataset.value; var a=STATE[field]||[];
      if(exclusive && v===exclusive){ a=[v]; }
      else { var i=a.indexOf(v); if(i<0){a=a.filter(function(x){return x!==exclusive;});a.push(v);} else a.splice(i,1); }
      STATE[field]=a; render();
    };
  });
}
function nextBtn(next){ screen.querySelector('[data-act="next"]').onclick=function(){go(next);}; }
function esc(s){ return String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;'); }

/* ===================================================================
   RESULTS LOGIC (recommendation engine — runs before the loader)
=================================================================== */
/* Build the loader lines from what we ACTUALLY asked this person. Each line is
   guarded by the answer its matching question writes, so the loader never claims
   to weigh a question that this path skipped (sleep/stress are only asked when a
   related goal is chosen; age, activity and location are on every path). */
function procSteps(){
  var steps=[];
  var gp=(STATE._goals||[]).map(function(g){return g.pack;})
    .filter(function(p){return p && p!=="FOUNDATION";})
    .map(function(p){return (/^[A-Z0-9]+$/.test(p.replace(/\s/g,"")) || p==="PMS") ? p : p.toLowerCase();});
  /* concerns — areas + concern list (always asked) */
  steps.push("Starting with what matters most to you"+(gp.length?": "+andList(gp):""));
  /* daily rhythm — activity (always asked), plus sleep/stress on those branches */
  if(STATE.activity||STATE.sleep||STATE.stress) steps.push("Thinking about how your days actually go");
  /* life stage — the age question */
  if(STATE.age) steps.push("Keeping where you are in life in mind");
  /* sunlight where they live — the location question */
  if(STATE.usState) steps.push("Taking the light where you live into account");
  /* closing beat — always */
  steps.push("Putting your set together");
  return steps;
}

/* Goals the person picked, lower-cased for a sentence, capped at three. Reads
   STATE.goals (their own picks), not the computed match, so the email gate can
   reflect their input back without giving the recommendation away. */
function gateGoals(){
  var seen={};
  return (STATE.goals||[]).map(function(g){return g.pack;})
    .filter(function(p){ if(!p||p==="FOUNDATION"||seen[p]) return false; seen[p]=1; return true; })
    .map(function(p){return (/^[A-Z0-9]+$/.test(p.replace(/\s/g,"")) || p==="PMS") ? p : p.toLowerCase();})
    .slice(0,3);
}

function computeSexBucket(){
  // Only "Female" or "Male" route to sex-specific packs; "Prefer not to say" gets
  // the neutral base, so no one is handed a pack made for a sex they didn't confirm.
  if(STATE.gender==="female") return "Female";
  if(STATE.gender==="male") return "Male";
  return "Neutral";
}
function resolveFoundation(){
  if(STATE.diet==="vegan") return "Vegan Nutrient";
  if(STATE.sex==="Male") return "Men's Wellness";
  if(STATE.sex==="Female") return "Women's Wellness";
  // Daily Foundation isn't a sellable product (no store listing), so the neutral
  // base is Immune — the one non-gendered pack whose "stay well every day"
  // framing reads naturally as a daily base.
  return "Immune"; // neutral / prefer-not-to-say
}

/* Sleep, stress and the mood safety check are each asked only to people who
   indicated interest in that area. */
function _g(g){ return g||STATE.goals||[]; }
function wantsSleepQ(g){ return _g(g).some(function(x){return x.pack==="Sleep";}); }
function wantsStressQ(g){ return _g(g).some(function(x){return x.pack==="Stress"||x.pack==="Mood Support";}); }
function wantsCrisisQ(g){ return _g(g).some(function(x){return x.pack==="Mood Support";}); }
function afterGoals(g){ return wantsSleepQ(g) ? "q10" : afterSleep(g); }
function afterSleep(g){ return wantsStressQ(g) ? "q11" : afterStress(g); }
function afterStress(g){ return wantsCrisisQ(g) ? "qMood" : "qSun"; }
/* Only ask what they already take if they said they take something in Q1. */
function q15OrSkip(){ return STATE.q1!=="none" ? "q15" : "q16restrict"; }

/* After the name step: a normal run asks for email next; an add-a-profile run
   reuses the buyer's email (already on the order) and skips straight to results. */
function nameNext(){
  if(ADDING_PROFILE && BUYER_EMAIL){
    STATE.email = BUYER_EMAIL;
    STATE.optMarketing = true;   // buyer already consented on the first profile
    track("email_reused_profile");
    buildResults();
    go("processing");
  } else {
    go("q18");
  }
}

/* Packs made specifically for one sex. */
var SEX_SPECIFIC = ["PMS","Perimenopause","Women's Hormone","Men's Hormone","Women's Wellness","Men's Wellness"];
function sexOK(pack){ return (STATE.sex==="Female"||STATE.sex==="Male") ? true : SEX_SPECIFIC.indexOf(pack)<0; }

/* Conditions only matter if one of the packs you're likely to see could be affected. */
var CONDITION_SENSITIVE = ["Sleep","Immune","Allergy","Mineral","Bone Builder"];
function likelyPacks(){
  var set=new Set();
  (STATE.goals||[]).forEach(function(g){set.add(g.pack==="FOUNDATION"?resolveFoundation():g.pack);});
  set.add(resolveFoundation());
  var primary=(STATE.goals||[]).find(function(g){return g.role==="primary";});
  var pr = primary ? (primary.pack==="FOUNDATION"?resolveFoundation():primary.pack) : null;
  if(pr && PAIRS[pr]) PAIRS[pr].forEach(function(x){set.add(x==="FOUNDATION"?resolveFoundation():x);});
  if(STATE.sleep==="lt5"||STATE.sleep==="56") set.add("Sleep");
  if(STATE.stress==="elevated"||STATE.stress==="high") set.add("Stress");
  var eat=STATE.lowEat||[];
  if(eat.indexOf("greens")>=0) set.add("Mineral");
  if(eat.indexOf("dairy")>=0) set.add("Bone Builder");
  return set;
}
function conditionsRelevant(){ var s=likelyPacks(); return CONDITION_SENSITIVE.some(function(p){return s.has(p);}); }
function nextAfterPregnancy(){ return conditionsRelevant() ? "q14" : q15OrSkip(); }
function nextAfterMeds(){ return (STATE.gender==="female" && STATE.age!=="60+") ? "q13" : nextAfterPregnancy(); }

/* Who can't or shouldn't take melatonin. */
function melatoninRule(){
  var meds=STATE.medKinds||[], cond=STATE.conditions||[];
  if(STATE.pregnancy==="Yes")
    return {level:"block", reason:"you're pregnant, breastfeeding, or trying"};
  if(cond.indexOf("autoimmune")>=0)
    return {level:"block", reason:"you flagged an autoimmune condition, and research indicates melatonin can affect the immune system"};
  if(["bloodthinner","bp","ssri","glp1"].some(function(k){return meds.indexOf(k)>=0;}))
    return {level:"block", reason:"it can interact with your medication"};
  if(STATE.meds==="Yes" && (meds.indexOf("prefer")>=0||meds.indexOf("other")>=0||meds.length===0)){
    var told = meds.indexOf("other")>=0 && (STATE.medOther||"").trim();
    return {level:"unverified", reason: told
      ? "we can't automatically check the medication you told us about"
      : "you didn't tell us which medication you take, so we couldn't check it"};
  }
  return {level:"ok", reason:""};
}

/* We never change a pack's formulation. safetyCheck only decides whether to
   RECOMMEND a pack or set it aside with a plain reason. */
function safetyCheck(pack){
  var meds=STATE.medKinds||[], cond=STATE.conditions||[], r=STATE.restrict||[];
  if(meds.indexOf("bloodthinner")>=0 && pack==="Bone Builder")
    return {ok:false, reason:"it contains vitamin K, which can interact with blood thinners"};
  if((meds.indexOf("thyroid")>=0||cond.indexOf("thyroidcond")>=0) && pack==="Mineral")
    return {ok:false, reason:"its calcium and iron need spacing from thyroid medication"};
  if(cond.indexOf("kidney")>=0 && (pack==="Bone Builder"||pack==="Mineral"))
    return {ok:false, reason:"high mineral and vitamin D levels can be hard on the kidneys, so it's worth checking with your doctor"};
  if(cond.indexOf("autoimmune")>=0 && (pack==="Immune"||pack==="Allergy"))
    return {ok:false, reason:"some immune-support ingredients can overstimulate an autoimmune condition, so it's worth checking with your doctor"};
  /* Manufacturer records (OK Capsule, checked 2026-09-27): Joint Health's
     glucosamine is GreenGrown®, plant-based and not shellfish-derived, and
     every Joint ingredient lists shellfish as absent. The one pack whose records
     don't confirm shellfish-free is Immune (the 14-mushroom blend). */
  if(r.indexOf("shellfish")>=0 && pack==="Immune")
    return {ok:false, reason:"we can't yet confirm its mushroom blend is shellfish-free"};
  if(STATE.diet==="vegan" && pack==="Joint Health")
    return {ok:false, reason:"its omega-3 is fish oil and its hyaluronic acid comes from chicken collagen"};
  return {ok:true};
}

/* What someone already buys that a recommended pack already includes. */
function supplementOverlap(chosenSet){
  var supps=STATE.currentSupps||[];
  if(!supps.length || supps.indexOf("Nothing")>=0) return [];
  var have=new Set();
  chosenSet.forEach(function(pack){(PACK_CONTAINS[pack]||[]).forEach(function(x){have.add(x);});});
  var labelMap={"Vit D":"vitamin D","Magnesium":"magnesium","Omega-3":"omega-3","Probiotic":"a probiotic","Multivitamin":"a multivitamin"};
  return supps.filter(function(s){return have.has(s);}).map(function(s){return labelMap[s]||s;});
}

/* ===================================================================
   v2 SCORING ENGINE  (replaces the old order-based priority)
   Each picked goal earns a GRADED score from the rest of the answers; the
   highest score leads. Tap order is ignored. Ties are impossible: the sort key
   ends in a globally-unique spine rank.
   - main pack   = top-scored surviving goal
   - we present only the 3 best-matched goals; extras become "also considered"
   - up to 2 support packs: picked secondary goals first, then any inferred
     pack that clears the evidence threshold
   - base pack always (droppable if they already take a multivitamin)
   - a chosen goal is NEVER docked; "already take" only annotates overlap
=================================================================== */
var SUPPORT_SLOTS = 2;     // main + up to 2 support + base => 4 packs max
var GOAL_CAP = 3;          // people can pick freely; we present only the 3 best-matched
var EV_THRESHOLD = 25;     // an INFERRED pack must clear this to be added
var EV_COVERAGE = 15;      // small nudge for a goal nothing else would cover (reserved)
var LIFESTAGE_BUMP_PACKS = new Set(["Perimenopause","PMS","Women's Hormone","Men's Hormone"]);
var BASE_PICK = 100;       // a pick always outranks any inference
/* A life-stage goal the person PICKED leads the set when it fits their age: it is
   the most time-specific thing they told us, and the lifestyle bumps (sitting,
   sun, diet) used to outrank it and hand e.g. a 50s woman who led with
   perimenopause a Joint Health pack (2026-09-26, Jack). Age-gated so an
   18–29 visitor who ticks Perimenopause isn't handed that pack as her match. */
var LIFESTAGE_BOOST = 200;
function lifestageFits(pack){
  var a=STATE.age;
  if(pack==="Perimenopause") return a==="40s"||a==="50s"||a==="60+";
  if(pack==="PMS") return a==="18-29"||a==="30s"||a==="40s";
  return LIFESTAGE_BUMP_PACKS.has(pack);
}

/* deterministic, globally-unique tiebreak spine = the order concerns are listed in */
var SPINE = ["Energy","Sleep","Stress","Focus","Mood Support","Longevity",
  "Gut","Microbiome","Immune","Joint Health","Allergy","Bone Builder",
  "PMS","Women's Hormone","Perimenopause","Men's Hormone",
  "Clear Skin","Hair","Beauty","Mineral","Brain","Performance","Antioxidant"];
function spineRank(p){ var i=SPINE.indexOf(p); return i<0?0:(SPINE.length-i); }

/* ---- pack ingredients (for the coverage tiebreak) ---- */
var PACK_INGREDIENTS = {
  "Allergy": ["D Complex", "NAC", "Omega 3", "Quercetin", "Trace Minerals", "Vitamin C"],
  "Antioxidant": ["Alpha Lipoic Acid", "NAC", "Quercetin", "Resveratrol", "Trace Minerals", "Vitamin C"],
  "Beauty": ["Biotin", "D Complex", "Hyaluronic Acid", "Omega 3", "Quercetin", "Resveratrol"],
  "Bone Builder": ["10B Probiotic", "Calcium Citrate", "D Complex", "Hyaluronic Acid", "Magnesium", "Trace Minerals"],
  "Brain": ["Ashwagandha", "Lion's Mane", "Methyl B-Complex", "Resveratrol", "Theanine"],
  "Clear Skin": ["10B Probiotic", "NAC", "Omega 3", "Turmeric", "Vitamin C", "Zinc"],
  "Energy": ["Ashwagandha", "Cordyceps", "Green Coffee Extract", "Methyl B-Complex", "Rhodiola", "Tyrosine"],
  "Eye & Vision": ["D Complex", "Omega 3", "Quercetin", "Resveratrol", "Trace Minerals", "Vitamin C", "Zinc"],
  "Focus": ["Lion's Mane", "Magnesium", "Rhodiola", "Theanine", "Tyrosine"],
  "Gut": ["10B Probiotic", "Glutamine", "Quercetin", "Soothing Fiber Formula", "Triphala"],
  "Hair": ["Biotin", "Hyaluronic Acid", "Omega 3", "Resveratrol", "Saw Palmetto", "Trace Minerals"],
  "Immune": ["10B Probiotic", "D Complex", "Mushroom Immune", "Vitamin C", "Zinc"],
  "Joint Health": ["Boron", "Hyaluronic Acid", "Omega 3", "Trace Minerals", "Vegan Glucosamine", "Vitamin C"],
  "Longevity": ["CoQ10", "Green Tea Extract", "NAC", "NR", "Quercetin", "Resveratrol"],
  "Men's Hormone": ["Boron", "DHEA", "DIM", "Fenugreek", "Tribulus", "Zinc"],
  "Men's Wellness": ["10B Probiotic", "Ashwagandha", "CoQ10", "D Complex", "Methyl B-Complex", "Multivitamin", "Omega 3"],
  "Microbiome": ["10B Probiotic", "Berberine", "Quercetin", "Resveratrol", "Soothing Fiber Formula", "Turmeric"],
  "Mineral": ["Boron", "Calcium Citrate", "Magnesium", "Trace Minerals"],
  "Mood Support": ["Ashwagandha", "Magnesium", "Methyl B-Complex", "Rhodiola", "Theanine", "Zinc"],
  "Muscle Preserve": ["BCAA", "Carnitine", "Creatine", "DHEA", "Magnesium", "Zinc"],
  "PMS": ["Ashwagandha", "Calcium Citrate", "DIM", "Magnesium", "Methyl B-Complex", "Omega 3"],
  "Performance": ["BCAA", "Carnitine", "Cordyceps", "Creatine", "NR", "Rhodiola"],
  "Perimenopause": ["Cordyceps", "DHEA", "DIM", "Fenugreek", "Magnesium"],
  "Sleep": ["Herbal Sleep Blend", "Magnesium", "Melatonin SR", "Theanine"],
  "Sleep (no Melatonin)": ["Herbal Sleep Blend", "Magnesium", "Theanine"],
  "Stress": ["Ashwagandha", "Cordyceps", "Magnesium", "Methyl B-Complex", "Omega 3", "Theanine"],
  "Vegan Nutrient": ["Calcium Citrate", "D Complex", "Iron", "Methyl B-Complex", "Trace Minerals", "Vegan Omega", "Zinc"],
  "Women's Hormone": ["Cordyceps", "DHEA", "DIM", "Fenugreek", "Omega 3"],
  "Women's Wellness": ["10B Probiotic", "Ashwagandha", "D Complex", "Multivitamin", "Omega 3", "Vitamin C"]
};
var PACK_MARGIN = { // gross margin $ per pack (Sell − Cost). Tiebreak only fires on a true score+coverage tie.
 'Microbiome':43.84,'Longevity':43.38,'Performance':42.40,'Hair':37.91,'Energy':37.48,
 'Antioxidant':37.14,'Allergy':37.14,'Bone Builder':36.75,'Beauty':36.41,"Men's Wellness":36.13,
 'Joint Health':36.05,'Gut':35.46,"Women's Wellness":34.38,'Eye & Vision':31.24,'Stress':31.20,
 'Mood Support':30.10,'Immune':28.78,'Focus':28.67,'Sleep (no Melatonin)':28.40,"Women's Hormone":28.20,
 'Brain':27.65,'Perimenopause':25.30,"Men's Hormone":25.30,'Mineral':23.10,'Muscle Preserve':22.70,
 'Vegan Nutrient':22.58,'Clear Skin':21.48,'PMS':19.10,'Sleep':18.95
};
/* coverage = how many of this pack's ingredients also appear in your OTHER picked goals */
function coverageScore(pack, picked){
  var mine = PACK_INGREDIENTS[pack]; if(!mine) return 0;
  var others = new Set();
  picked.forEach(function(p){ if(p!==pack){ (PACK_INGREDIENTS[p]||[]).forEach(function(i){others.add(i);}); } });
  return mine.filter(function(i){return others.has(i);}).length;
}

/* what each "already take" answer represents, in ingredient terms. We never remove
   a pack for partial overlap — we highlight what's already inside it. */
var SUPP_COVERS = {
  "Multivitamin":['Multivitamin','D Complex','Trace Minerals','Zinc','Methyl B-Complex','Vitamin C','Calcium Citrate','Magnesium'],
  "Magnesium":['Magnesium'], "Vit D":['D Complex'], "Omega-3":['Omega 3','Vegan Omega'], "Probiotic":['10B Probiotic']
};
var SUPP_NAME = { "Multivitamin":"a multivitamin","Magnesium":"magnesium","Vit D":"vitamin D","Omega-3":"omega-3","Probiotic":"a probiotic" };
function alreadyInside(pack){
  var have=new Set();
  (STATE.currentSupps||[]).forEach(function(a){ if(a==='Multivitamin') return; (SUPP_COVERS[a]||[]).forEach(function(i){have.add(i);}); }); // a multivitamin only drops the base
  return (PACK_INGREDIENTS[pack]||[]).filter(function(i){return have.has(i);});
}

/* graded evidence a pack earns from the rest of the answers (degree, not presence) */
function corroboration(pack){
  var ev=0;
  var sleepEv  = STATE.sleep==="lt5"?50 : STATE.sleep==="56"?30 : 0;
  var stressEv = STATE.stress==="high"?50 : STATE.stress==="elevated"?30 : 0;
  var sunEv    = STATE.sun==="long"?30 : STATE.sun==="mid"?15 : 0; // "long" = low-sun states
  var eat=STATE.lowEat||[];
  if(pack==="Sleep")  ev += sleepEv + stressEv;
  if(pack==="Energy") ev += sleepEv;
  if(pack==="Stress") ev += stressEv + sleepEv;
  if(pack==="Mood Support") ev += stressEv;
  /* no fish bump for Focus/Brain: neither pack contains omega-3, so "you rarely
     eat fish" is not evidence for them (the daily base carries the Omega 3) */
  if(pack==="Mineral"){ if(eat.indexOf("greens")>=0) ev += 40; ev += sunEv; }
  if(pack==="Bone Builder"){ if(eat.indexOf("dairy")>=0) ev += 40; ev += sunEv; }
  if(pack==="Joint Health" && STATE.activity==="sitting") ev += 25;
  if(pack==="Performance" && (STATE.activity==="active"||STATE.activity==="athlete")) ev += 25;
  return ev;
}

/* packs the lifestyle answers would surface on their own, with their reason */
function inferredCandidates(chosen){
  var out=[], eat=STATE.lowEat||[];
  var push=function(pack,reason){ if(!chosen.has(pack)) out.push({pack:pack,reason:reason,ev:Math.min(99, corroboration(pack))}); }; // inferred never reaches the pick floor
  if(STATE.sleep==="lt5"||STATE.sleep==="56") push("Sleep","based on what you told us about your sleep");
  if(STATE.stress==="elevated"||STATE.stress==="high") push("Stress","based on what you told us about your stress");
  if(eat.indexOf("greens")>=0) push("Mineral","because you rarely eat leafy greens");
  if(eat.indexOf("dairy")>=0)  push("Bone Builder","because you rarely eat dairy");
  if(STATE.activity==="sitting") push("Joint Health","because you told us you sit for most of the day");
  if(STATE.activity==="athlete") push("Performance","because working out is a big part of your life");
  else if(STATE.activity==="active") push("Performance","because you told us you train regularly");
  return out;
}

function buildResults(){
  var goals = STATE.goals.map(function(g){return {role:g.role, pack:g.pack==="FOUNDATION"?resolveFoundation():g.pack};});
  var seen=new Set(); goals=goals.filter(function(g){if(seen.has(g.pack))return false;seen.add(g.pack);return true;});

  STATE._setAside=[];
  // keep goals that pass sex + safety; set-aside the rest with a reason
  var kept=[];
  goals.forEach(function(g){
    if(!sexOK(g.pack)) return;
    var sc=safetyCheck(g.pack);
    if(!sc.ok){ STATE._setAside.push({pack:g.pack,reason:sc.reason}); }
    else kept.push(g);
  });

  var base = resolveFoundation();

  // ---- score every surviving goal (graded; a chosen goal is never docked) ----
  var keptPacks = kept.map(function(g){return g.pack;});
  var scored = kept.map(function(g){
    var corr=corroboration(g.pack);                                  // other answers add evidence
    var lifeBoost=(LIFESTAGE_BUMP_PACKS.has(g.pack)&&lifestageFits(g.pack))?LIFESTAGE_BOOST:0; // picked + age-appropriate life stage leads
    /* When a 40+ woman picks perimenopause alongside the general hormone pack,
       perimenopause is the more specific match (hot flashes, cycles): it leads. */
    if(g.pack==="Perimenopause" && lifeBoost) lifeBoost+=10;
    var ev=BASE_PICK+corr+lifeBoost;                                 // picked -> 100, plus evidence
    var cover=coverageScore(g.pack, keptPacks);                      // tiebreak when totals tie
    var margin=PACK_MARGIN[g.pack]||0;
    var lifestage = LIFESTAGE_BUMP_PACKS.has(g.pack)?1:0;
    return { pack:g.pack, ev:ev, base:BASE_PICK, corr:corr, lifeBoost:lifeBoost, cover:cover, margin:margin, lifestage:lifestage };
  });
  // highest total wins; ties -> coverage -> margin -> unique spine
  scored.sort(function(a,b){ return b.ev-a.ev || b.cover-a.cover || b.margin-a.margin || spineRank(b.pack)-spineRank(a.pack); });

  // ---- present only the 3 best-matched, even when they pick more ----
  // Stage 1 — which goals make the set: FIT only (score, coverage, list order). Never margin.
  var byFit = scored.slice().sort(function(a,b){ return b.ev-a.ev || b.cover-a.cover || spineRank(b.pack)-spineRank(a.pack); });
  var topGoals = byFit.slice(0, GOAL_CAP);
  var alsoPicked = byFit.slice(GOAL_CAP);
  // Stage 2 — order the ones that made it; margin may break a tie for the headline between in-set packs.
  var ordered = topGoals.slice().sort(function(a,b){ return b.ev-a.ev || b.cover-a.cover || b.margin-a.margin || spineRank(b.pack)-spineRank(a.pack); });

  STATE._goals = ordered.map(function(s,i){ return {role:i===0?"primary":"secondary", pack:s.pack}; });
  STATE._goalScores={}; scored.forEach(function(s){ STATE._goalScores[s.pack]=s.ev; });
  // goals they picked that ranked just below the top 3 — shown on results, never silently dropped
  STATE._alsoConsidered = alsoPicked.map(function(g){
    var shared = ordered.map(function(t){return t.pack;}).filter(function(tp){
      return (PACK_INGREDIENTS[tp]||[]).some(function(i){ return (PACK_INGREDIENTS[g.pack]||[]).indexOf(i)>=0; });
    });
    return { pack:g.pack, reason: shared.length
      ? "Ranked just below your top three. Your "+shared[0]+" pack already shares some of it."
      : "Ranked just below your top three." };
  });
  var chosen = new Set(STATE._goals.map(function(g){return g.pack;}));

  // ---- fill up to SUPPORT_SLOTS: secondary goals first, then inferred >= threshold ----
  STATE._upsells=[]; STATE._nudges={}; STATE._notes={};
  var secondaryGoals = STATE._goals.slice(1);            // already ranked by score
  var slots = SUPPORT_SLOTS - Math.min(secondaryGoals.length, SUPPORT_SLOTS);
  var inferred = inferredCandidates(chosen)
      .filter(function(c){ return c.ev>=EV_THRESHOLD && sexOK(c.pack); })
      .filter(function(c){ var sc=safetyCheck(c.pack); if(sc.ok) return true; STATE._setAside.push({pack:c.pack,reason:sc.reason}); return false; });
  inferred.sort(function(a,b){ return b.ev-a.ev || spineRank(b.pack)-spineRank(a.pack); });
  for(var ii=0; ii<inferred.length; ii++){
    var c=inferred[ii];
    if(slots<=0) break;
    if(chosen.has(c.pack)) continue;
    STATE._upsells.push(c.pack); chosen.add(c.pack); STATE._nudges[c.pack]=c.reason; slots--;
  }

  // Antioxidant partners the Longevity goal (competes for a support slot; bumps only a weak inferred add-on)
  if(chosen.has('Longevity') && !chosen.has('Antioxidant') && sexOK('Antioxidant') && safetyCheck('Antioxidant').ok){
    var aReason='partners your Longevity pack, supporting your cells against everyday oxidative stress';
    if(slots>0){ STATE._upsells.push('Antioxidant'); chosen.add('Antioxidant'); STATE._nudges['Antioxidant']=aReason; slots--; }
    else if(STATE._upsells.length){ var bumped=STATE._upsells.pop(); chosen.delete(bumped); delete STATE._nudges[bumped];
      STATE._upsells.push('Antioxidant'); chosen.add('Antioxidant'); STATE._nudges['Antioxidant']=aReason; }
  }

  // all goals removed for safety, or foundation-only -> base stands in as the main pack
  if(STATE._goals.length===0){ STATE._goals=[{role:"primary",pack:base}]; }

  // ---- base pack (always present; droppable if they already take a multivitamin) ----
  STATE._basePack = base;
  var baseIsGoal = STATE._goals.some(function(g){return g.pack===base;});
  STATE._baseDroppable = (STATE.currentSupps||[]).indexOf("Multivitamin")>=0 && !baseIsGoal;
  if(!baseIsGoal) chosen.add(base);

  /* "Because you told us" notes: traceable, never diagnostic */
  var note=function(p,t){ STATE._notes[p]=(STATE._notes[p]?STATE._notes[p]+" ":"")+t; };
  if(chosen.has("Sleep") && chosen.has("Stress"))
    note("Stress","Paired with your Sleep pack on purpose: both lean on magnesium, which supports rest and a steadier response to stress.");
  if(STATE.sun==="long")
    note(base,"It includes vitamin D, which counts for more where you are: the body makes less of it through long, dark winters.");
  if(STATE.diet==="vegan" && base==="Vegan Nutrient")
    note(base,"Chosen because you eat plant-based.");
  if((STATE.lowEat||[]).indexOf("redmeat")>=0)
    note(base,"It covers iron and B12, which matter more since you rarely eat red meat.");
  if(STATE.activity==="sitting" && STATE.sun!=="long")
    note(base,"It includes vitamin D, which matters more when most of your day is spent indoors.");
  if((STATE.activity==="active"||STATE.activity==="athlete"||STATE.activity==="onfeet") && chosen.has("Energy"))
    note("Energy","Chosen to match how much you're on the go.");
  if(STATE._baseDroppable)
    note(base,"You already take a multivitamin, which covers the same ground. Keep it for the full foundation, or skip it.");

  // highlight what someone already takes that is already inside each pack, and what it adds
  STATE._goals.map(function(g){return g.pack;}).concat(STATE._upsells).forEach(function(pk){
    if(pk===base) return;
    var have=alreadyInside(pk);
    if(have.length){
      var adds=(PACK_INGREDIENTS[pk]||[]).filter(function(i){return have.indexOf(i)<0;}).slice(0,3);
      note(pk, "Includes the "+have.join(', ')+" you already take"+(adds.length?", and adds "+adds.join(', '):"")+".");
    }
  });

  STATE.checked={};
  STATE._goals.forEach(function(g){STATE.checked[g.pack]=true;});
  STATE._upsells.forEach(function(p){STATE.checked[p]=true;});
  STATE.checked[base]=true;

  // dedupe set-aside list
  var sa=new Set(); STATE._setAside=STATE._setAside.filter(function(s){if(sa.has(s.pack))return false;sa.add(s.pack);return true;});

  // savings overlap (a note, not a removal)
  STATE._overlap = supplementOverlap(chosen);

  // melatonin: only relevant if Sleep is in the set
  var m=melatoninRule(); STATE.melatoninLevel=m.level; STATE.melatoninReason=m.reason;
  if(chosen.has("Sleep")){
    STATE.melatoninScreened=true;
    STATE.sleepMelatonin = (m.level==="ok");
  } else {
    STATE.melatoninScreened=false;
  }
}

function andList(a){ return a.length<=1?(a[0]||"") : a.slice(0,-1).join(", ")+" and "+a[a.length-1]; }

/* ===================================================================
   PRODUCTION WIRING — derive signals, capture, hand off
=================================================================== */
/* recommended_pack is the matched pack's product-handle stem ("microbiome" for
   /products/microbiome-pack). Klaviyo's assessment emails build both the pack name
   ({{ recommended_pack|title }} Pack) and the product link
   (/products/{{ recommended_pack }}-pack) from it, so it has to name the pack the
   results page actually shows. The old six-value legacy map sent every unmapped
   pack (Microbiome, Energy, Beauty...) to "antioxidant". */
var PACK_KEY_ALIAS = { "Daily Foundation":"immune" };  // retired pack; results shows Immune
function packKey(pack){
  return PACK_KEY_ALIAS[pack] || String(pack||"").toLowerCase().replace(/['\u2019]/g,"").replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");
}
/* recommended_pack_name is the same pack as the buyer reads it ("Mood Support",
   "Women's Hormone"), for emails to print. |title on the handle stem can't restore
   spaces or apostrophes ("Mood-Support", "Womens-Hormone"). */
var PACK_NAME_ALIAS = { "Daily Foundation":"Immune" };
function packDisplayName(pack){ return PACK_NAME_ALIAS[pack] || String(pack||""); }

var PACK_TO_CONCERN = {
  "Energy":"energy","Performance":"performance","Longevity":"energy",
  "Sleep":"sleep","Stress":"stress","Mood Support":"stress","Focus":"focus","Brain":"focus",
  "Immune":"immunity","Allergy":"immunity","Joint Health":"performance","Bone Builder":"performance",
  "Gut":"gut","Microbiome":"gut","Clear Skin":"skin","Beauty":"skin","Hair":"skin",
  "PMS":"hormones","Women's Hormone":"hormones","Perimenopause":"hormones","Men's Hormone":"hormones",
  "Women's Wellness":"energy","Men's Wellness":"energy","Daily Foundation":"energy",
  "Vegan Nutrient":"energy","Antioxidant":"energy","Mineral":"energy"
};
function concernFor(pack){ return PACK_TO_CONCERN[pack] || "energy"; }

function primaryPackName(){
  var goals = STATE._goals || [];
  var primary = goals.find(function(g){return g.role==="primary";}) || goals[0];
  return primary ? (primary.pack==="FOUNDATION"?resolveFoundation():primary.pack) : resolveFoundation();
}

function deriveSignals(){
  var primaryPack = primaryPackName();
  var primary_concern = concernFor(primaryPack);
  var recommended_pack = packKey(primaryPack);
  var recommended_pack_name = packDisplayName(primaryPack);

  // complementary: pack keys from the upsells, in order, deduped, excluding the
  // recommended one — then topped up with sensible defaults.
  var complementary = [];
  (STATE._upsells||[]).forEach(function(p){
    var k=packKey(p);
    if(k!==recommended_pack && complementary.indexOf(k)<0 && complementary.length<2) complementary.push(k);
  });
  ["antioxidant","immune","sleep"].forEach(function(k){
    if(k!==recommended_pack && complementary.indexOf(k)<0 && complementary.length<2) complementary.push(k);
  });

  var stage = STATE.age || "general";
  return { primary_concern:primary_concern, segment:primary_concern+"-"+stage, recommended_pack:recommended_pack, recommended_pack_name:recommended_pack_name, complementary:complementary };
}

/* ---- Klaviyo (client-side, public ID only) ----
   complete() redirects to the results page the instant this returns, so the
   onsite SDK's identify/track lost the race and nothing ever landed (verified
   live: no profile created, and the __kla_id cookie never gained $email).
   We POST to Klaviyo's public client endpoints with keepalive instead - the
   same trick setCartAttributes() already uses to survive the same redirect.
   These endpoints take the PUBLIC company id only, so no secret ships here. */
var KLAVIYO_REVISION = '2024-10-15';
function klaviyoPost(path, payload){
  try{
    fetch('https://a.klaviyo.com/client/'+path+'/?company_id='+encodeURIComponent(KLAVIYO_ID), {
      method:'POST',
      keepalive:true, /* survives the immediate redirect to the results page */
      headers:{ 'Content-Type':'application/json', 'revision':KLAVIYO_REVISION },
      body:JSON.stringify(payload)
    }).catch(function(){});
  }catch(e){}
}
/* ---- Assessment answers kept on the profile ----
   Every answer the assessment collects EXCEPT the medical screening. The
   omissions below are deliberate - do not add them without a privacy-policy
   change, and note that Klaviyo is a marketing platform, not a health record:
     crisis                     - the self-harm safety check (qMood)
     meds / medKinds / medOther - prescription medication
     pregnancy                  - pregnancy / breastfeeding / TTC status
     conditions                 - thyroid, autoimmune, kidney
     melatonin* / sleepMelatonin- derived FROM the above; melatoninReason
                                  quotes the medical answer back verbatim
   Shellfish and soy allergies are dropped from the dietary answer for the
   same reason: only the vegan flag is kept. */
function answerProps(){
  var restrict = STATE.restrict || [];
  return {
    quiz_current_regimen:   STATE.q1 || null,
    quiz_gender:            STATE.gender || null,
    quiz_sex_bucket:        STATE.sex || null,
    quiz_age:               STATE.age || null,
    quiz_focus_areas:       (STATE.areasOrdered||[]).slice(),
    quiz_goal_packs:        (STATE.goals||[]).map(function(g){ return (g && g.pack) || g; })
                              .filter(function(x){ return typeof x === 'string'; }),
    quiz_diet:              STATE.diet || null,
    quiz_vegan:             restrict.indexOf('vegan') > -1,
    quiz_eats_regularly:    (STATE.eatRegular||[]).slice(),
    quiz_low_intake:        (STATE.lowEat||[]).slice(),
    quiz_activity:          STATE.activity || null,
    quiz_sleep_hours:       STATE.sleep || null,
    quiz_stress:            STATE.stress || null,
    quiz_sun_tier:          STATE.sun || null,
    quiz_state:             STATE.usState || null,
    quiz_supplements_taken: (STATE.currentSupps||[]).slice()
  };
}
function sendToKlaviyo(signals){
  var props={ primary_concern:signals.primary_concern, segment:signals.segment, recommended_pack:signals.recommended_pack, recommended_pack_name:signals.recommended_pack_name, accepts_marketing:!!STATE.optMarketing };
  var lpv=lpVariant(); if(lpv) props.lp_variant=lpv;   /* LP test 2026-09-29 */
  var answers=answerProps();
  for(var k in answers){ if(Object.prototype.hasOwnProperty.call(answers,k)) props[k]=answers[k]; }
  if(!KLAVIYO_ID){ if(window.console) console.log('[Klaviyo placeholder] identify+track would send', {email:STATE.email, properties:props}); return; }
  if(!STATE.email) return;                 /* nothing to attach the signals to */

  var profile={ email:STATE.email, properties:props };
  if(STATE.name) profile.first_name=STATE.name;

  /* profile properties (was kl.identify) */
  klaviyoPost('profiles', { data:{ type:'profile', attributes:profile } });

  /* the Completed Quiz event Klaviyo flows trigger on (was kl.track) */
  klaviyoPost('events', { data:{ type:'event', attributes:{
    properties: props,
    metric:  { data:{ type:'metric',  attributes:{ name:'Completed Quiz' } } },
    profile: { data:{ type:'profile', attributes:profile } }
  } } });

  /* Subscribe to the marketing list, but only with consent. Without this the
     profile exists and the event fires, yet nobody is actually subscribed and
     no flow can email them. */
  if(KLAVIYO_LIST && STATE.optMarketing){
    klaviyoPost('subscriptions', { data:{
      type:'subscription',
      attributes:{
        custom_source:'Assessment',
        profile:{ data:{ type:'profile', attributes:profile } }
      },
      relationships:{ list:{ data:{ type:'list', id:KLAVIYO_LIST } } }
    } });
  }

  /* Best-effort: if the Klaviyo app embed already put the SDK on the page,
     identify there too so the browser cookie carries the identity into onsite
     personalisation. Deliberately never injects the script - that fetch is
     what lost the race in the first place. */
  try{
    if(window.klaviyo && window.klaviyo.identify){
      var ident={ email:STATE.email, properties:props };
      if(STATE.name) ident.first_name=STATE.name;   /* name is optional: omit rather than send "" */
      window.klaviyo.identify(ident);
    }
  }catch(e){}
}

/* ---- Cart attributes (land on the order) ---- */
function setCartAttributes(signals){
  try{
    fetch('/cart/update.js', {
      method:'POST',
      keepalive:true, /* survives the immediate redirect to the results page */
      headers:{ 'Content-Type':'application/json', 'X-Requested-With':'XMLHttpRequest' },
      body:JSON.stringify({ attributes:{
        quiz_primary_concern:signals.primary_concern,
        quiz_segment:signals.segment,
        quiz_recommended_pack:signals.recommended_pack,
        quiz_accepts_marketing:STATE.optMarketing ? 'yes' : 'no'
      } })
    }).catch(function(){});
  }catch(e){}
}

/* ---- Completion: build handoff, fire wiring, redirect ---- */
function complete(){
  if(!STATE._goals) buildResults();     // defensive: ensure the recommendation exists
  var signals = deriveSignals();
  var result = {
    v: 2,
    signals: signals,                   // legacy summary for the current results page
    firstName: STATE.name || '',
    email: STATE.email || '',
    acceptsMarketing: !!STATE.optMarketing,
    quiz: STATE                         // full answers + derived recommendation for the new results page
  };
  // Safari private-mode (and any quota-exhausted browser) can throw on BOTH
  // storage writes below; if neither lands, the results page finds nothing and
  // shows the empty state. Track success so we have a fallback for that case.
  var stored = false;
  try{ sessionStorage.setItem(RESULT_KEY, JSON.stringify(result)); stored = true; }catch(e){}
  try{ localStorage.setItem(DONE_KEY, JSON.stringify(result)); stored = true; }catch(e){}
  try{ sessionStorage.removeItem(SKEY); }catch(e){}   // in-progress answers no longer needed
  sendToKlaviyo(signals);
  setCartAttributes(signals);
  var dest = RESULTS_URL;
  if(!stored){
    // last resort: carry the payload in the URL fragment (never sent to the
    // server) so the results page can still render. base64 keeps it URL-safe.
    try{ dest = RESULTS_URL + '#r=' + encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(result))))); }catch(e){}
  }
  window.location.href = dest;
}

/* ===================================================================
   VIEW (phone vs desktop) — sets [data-view] on the quiz root
=================================================================== */
function setView(v){ root.dataset.view=v; }
var mqPhone = window.matchMedia("(max-width:560px)");
function autoView(){ setView(mqPhone.matches ? "phone" : "desktop"); }
autoView();
if(mqPhone.addEventListener) mqPhone.addEventListener("change",autoView);
else if(mqPhone.addListener) mqPhone.addListener(autoView);

/* ===================================================================
   BOOT
=================================================================== */
/* LP test 2026-09-29: which arm of the /pages/routine test this browser is in
   ("lp" saw the landing page, "direct" was sent straight here). null = never
   came through the landing page. */
function lpVariant(){
  /* URL first (?lpv= rides along from the landing page or the redirect), then
     storage. Storage alone missed people whose in-app browser blocks it or who
     hopped to another browser between the landing page and the assessment. */
  var m=/[?&]lpv=(lp|direct)\b/.exec(location.search), v=m?m[1]:null;
  if(v){ try{ sessionStorage.setItem('capsi_lp_variant',v); }catch(e){} try{ localStorage.setItem('capsi_lp_variant',v); }catch(e){} return v; }
  try{ v=sessionStorage.getItem('capsi_lp_variant'); }catch(e){}
  if(v!=='lp' && v!=='direct'){ try{ v=localStorage.getItem('capsi_lp_variant'); }catch(e){ v=null; } }
  return (v==='lp'||v==='direct') ? v : null;
}
(function(){ var v=lpVariant(); if(v && typeof window.clarity==='function'){ try{ window.clarity('set','lp_variant',v); }catch(e){} } })();
function reset(){
  STATE=fresh(); current="q1"; history=[]; lastRoute=null;
  try{ sessionStorage.removeItem(SKEY); }catch(e){}
  try{ window.history.replaceState({i:0},""); }catch(e){}
  track("quiz_start", lpVariant() ? {lp_variant:lpVariant()} : undefined);
  render();
}

/* Header Back: step to the previous question. */
var qzback = document.getElementById('qzback');
if(qzback) qzback.onclick=function(){ if(history.length) back(); };

/* Header Exit: confirm before leaving (progress stays saved in sessionStorage). */
var qzleave = document.getElementById('qzleave');
var qzexit = document.getElementById('qzexit');
function leaveEsc(e){ if(e.key==="Escape") closeLeave(); }
function openLeave(){ if(qzleave){ qzleave.hidden=false; document.addEventListener('keydown',leaveEsc); } }
function closeLeave(){ if(qzleave){ qzleave.hidden=true; document.removeEventListener('keydown',leaveEsc); } }
if(qzexit) qzexit.onclick=openLeave;
var qzstay = document.getElementById('qzleave-stay');
if(qzstay) qzstay.onclick=closeLeave;
if(qzleave) qzleave.addEventListener('click',function(e){ if(e.target===qzleave) closeLeave(); });

/* A visitor who wants to start over arrives with ?restart=1 (or ?retake=1):
   wipe saved progress + any finished result and run the quiz fresh. */
/* "Add another profile" (?addprofile=1): start a FRESH quiz for the next person.
   The prior person's packs are already in the shared Shopify cart (and remembered
   in the profiles list), so we drop this device's saved answers/handoff/last-result
   — but KEEP the profiles list so the shared order survives. If this second quiz
   is interrupted, the visitor resumes it rather than jumping back to the old result. */
/* Back from the results page lands on one of the finished quiz's step entries
   (one pushState per question). Redirecting forward again traps the visitor:
   GA showed phones bouncing quiz <-> results 20+ times while trying to leave.
   When we got here via Back/Forward, skip every quiz entry in one jump instead.
   Entries carry {i: step}; the first quiz entry is i=0, so i+1 steps back lands
   on whatever came before the quiz (the landing page, the ad, the app). */
function leaveFinishedQuiz(){
  var nav=null;
  try{ nav=(performance.getEntriesByType('navigation')[0]||{}).type; }catch(e){}
  var st=null; try{ st=window.history.state; }catch(e){}
  if(nav!=='back_forward' || !st || typeof st.i!=='number') return false;
  try{ window.history.go(-(st.i+1)); return true; }catch(e){ return false; }
}
/* Same case when the browser restores this page from the back/forward cache
   (boot code above does not re-run then). */
window.addEventListener('pageshow',function(e){
  if(!e.persisted) return;
  try{
    var d=JSON.parse(localStorage.getItem(DONE_KEY)||'null');
    var st=window.history.state;
    if(d && d.signals && st && typeof st.i==='number') window.history.go(-(st.i+1));
  }catch(err){}
});

var wantsAddProfile = /[?&]addprofile=1\b/.test(location.search);
var wantsRestart = /[?&](restart|retake)=1\b/.test(location.search);
if(wantsAddProfile){
  try{ sessionStorage.removeItem(SKEY); }catch(e){}        // drop any in-progress answers
  try{ sessionStorage.removeItem(RESULT_KEY); }catch(e){}  // drop the current-tab handoff
  try{ localStorage.removeItem(DONE_KEY); }catch(e){}      // drop last-result (keep capsi_quiz_profiles)
  ADDING_PROFILE = true;
  try{ BUYER_EMAIL = localStorage.getItem(BUYER_EMAIL_KEY) || ''; }catch(e){ BUYER_EMAIL=''; }
  try{ var _pl=JSON.parse(localStorage.getItem('capsi_quiz_profiles')||'[]')||[];
       EXISTING_NAMES=_pl.map(function(p){return String(p.name||'').trim().toLowerCase();}).filter(Boolean); }catch(e){ EXISTING_NAMES=[]; }
  var pn=document.getElementById('qzprofilenote'); if(pn) pn.hidden=false; // reassure: order is saved
  try{
    var ap=new URL(location.href);
    ap.searchParams.delete('addprofile');
    window.history.replaceState(null,'',ap.pathname+ap.search+ap.hash);
  }catch(e){}
  reset();
} else if(wantsRestart){
  try{ sessionStorage.removeItem(SKEY); }catch(e){}
  try{ localStorage.removeItem(DONE_KEY); }catch(e){}
  try{ sessionStorage.removeItem(RESULT_KEY); }catch(e){}
  try{ localStorage.removeItem('capsi_quiz_profiles'); }catch(e){} // a true restart clears the shared order too
  try{ localStorage.removeItem(BUYER_EMAIL_KEY); }catch(e){}
  try{ localStorage.removeItem('capsi_quiz_plan'); }catch(e){}
  try{
    var ru=new URL(location.href);
    ru.searchParams.delete('restart'); ru.searchParams.delete('retake');
    window.history.replaceState(null,'',ru.pathname+ru.search+ru.hash);
  }catch(e){}
  reset();
} else {
  /* Already completed the quiz? Jump straight to the saved results. */
  var jumped=false;
  try{
    var doneRaw=localStorage.getItem(DONE_KEY);
    var done=doneRaw?JSON.parse(doneRaw):null;
    if(done && done.signals){
      if(leaveFinishedQuiz()){ jumped=true; }
      else {
        try{ if(!sessionStorage.getItem(RESULT_KEY)) sessionStorage.setItem(RESULT_KEY, doneRaw); }catch(e){}
        location.replace(RESULTS_URL);
        jumped=true;
      }
    }
  }catch(e){}
  if(!jumped){
    if(loadSession()){
      // never resume mid-loader; if they'd reached the loader, just finish + redirect
      if(current==="processing"){ complete(); }
      else { try{ window.history.replaceState({i:history.length},""); }catch(e){} lastRoute=null; render(); }
    } else {
      reset();
    }
  }
}
})();
